<script setup lang="ts">
import { computed, ref } from 'vue';

import ULoader from '../ULoader/ULoader.vue';
import { formatFileSize } from '../UFileUploader/types';
import { getFileIcon } from './types';
import type { FileAttachmentProps } from './types';

const props = withDefaults(defineProps<FileAttachmentProps>(), {
  clearable: false,
  uploading: false,
  error: false,
  errorMessage: '',
  resolveDownloadUrl: undefined,
});

const emit = defineEmits<{
  remove: [];
}>();

const isDownloading = ref(false);

const fileName = computed(() => props.file.meta.name ?? 'Unknown file');
const fileSize = computed(() => formatFileSize(props.file.meta.size ?? 0));
const fileIcon = computed(() => getFileIcon(props.file.meta.mimeType));
const isImage = computed(() => {
  const mime = props.file.meta.mimeType;

  return mime?.startsWith('image/') ?? false;
});

const thumbnailUrl = computed(() => {
  if (isImage.value && !props.error && props.file.url) {
    return props.file.url;
  }

  return null;
});

const errorText = computed(() => props.errorMessage || 'Failed to upload the file. Please try again.');

/**
 * Prefer `downloadUrl` — presigned GET with Content-Disposition: attachment —
 * fall back to `url` for public files served directly from the bucket.
 */
const downloadHref = computed(() => props.file.downloadUrl ?? props.file.url ?? null);

async function handleDownload() {
  if (!downloadHref.value || isDownloading.value) {
    return;
  }

  isDownloading.value = true;

  try {
    const renewed = await props.resolveDownloadUrl?.().catch(() => null);
    const link = document.createElement('a');
    link.href = renewed ?? downloadHref.value;
    link.rel = 'noopener';
    if (props.file.meta.name) {
      link.download = props.file.meta.name;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    isDownloading.value = false;
  }
}
</script>

<template>
  <div
    :class="[
      'rounded-[10px] p-3 flex gap-3 items-center border',
      error
        ? 'bg-error/10 border-error/40'
        : 'bg-accented border-transparent',
    ]"
    :data-testid="error ? 'file-attachment-error' : undefined"
  >
    <!-- Image Thumbnail or Icon -->
    <div
      v-if="thumbnailUrl"
      class="shrink-0 size-[34px] rounded-[4px] overflow-hidden bg-default"
    >
      <img
        :src="thumbnailUrl"
        :alt="fileName"
        class="size-full object-cover"
      >
    </div>
    <div
      v-else
      :class="['shrink-0 size-[34px] flex items-center justify-center', error ? 'text-error' : 'text-toned']"
    >
      <UIcon
        :name="error ? 'i-lucide-alert-triangle' : fileIcon"
        class="size-5"
      />
    </div>

    <!-- File Info -->
    <div class="flex-1 min-w-0 flex flex-col gap-0.5">
      <div class="flex items-center gap-2">
        <p :class="['text-xs truncate', error ? 'text-error font-medium' : 'text-default']">
          {{ fileName }}
        </p>
        <ULoader
          v-if="uploading"
          size="xs"
          :ui="{ root: 'm-0' }"
        />
      </div>
      <p
        v-if="error"
        class="text-xs text-error"
        data-testid="file-attachment-error-message"
      >
        {{ errorText }}
      </p>
      <p
        v-else
        class="text-xs text-dimmed"
      >
        {{ uploading ? 'Loading…' : fileSize }}
      </p>
    </div>

    <!-- Actions -->
    <div class="flex items-center gap-2">
      <!-- Download Button -->
      <button
        v-if="downloadHref && !error"
        type="button"
        class="shrink-0 text-primary hover:text-primary/80 transition-colors p-1 disabled:opacity-50 disabled:cursor-wait"
        title="Download"
        :disabled="isDownloading"
        data-testid="file-attachment-download-button"
        @click="handleDownload"
      >
        <UIcon
          :name="isDownloading ? 'i-lucide-loader-2' : 'i-lucide-download'"
          :class="['size-4', { 'animate-spin': isDownloading }]"
        />
      </button>

      <!-- Remove Button -->
      <button
        v-if="clearable"
        type="button"
        :class="[
          'shrink-0 transition-colors p-1',
          error ? 'text-error hover:text-error/80' : 'text-muted hover:text-default',
        ]"
        :title="error ? 'Remove' : 'Delete'"
        data-testid="file-attachment-remove-button"
        @click="emit('remove')"
      >
        <UIcon
          name="i-lucide-x"
          class="size-4"
        />
      </button>
    </div>
  </div>
</template>
